import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    TextInput,
    Text,
    Services,
    FormModal,
    Loader,
    Theme,
    DateInput,
    Checkbox,
    i18n,
    tokens
} from '@simplybenefits/sb-ui-library';
import * as Dates from '@simplybenefits/sb-dates';
import { optOut, optIn } from '../../actions/marketplace';
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";

class ProductOptOutModal extends PureComponent {
    state = {
        page: 1,
        showOptOut: false,
        visibility: false,
        password: '',
        signage: {
            name: '',
            signature: '',
            acceptedTerms: false,
            timestampOfAcceptance: null,
            datedOn: null
        }
    };

    _optOut = async () => {
        const signageValid = await this._validateSignageBlock();

        if (!signageValid) {
            return false;
        }

        const productID = this.props.productsToReview[this.state.page - 1];
        const numberOfProducts = this.props.productsToReview.length;

        // send the opt out request to the backend
        const success = await this.props.dispatch(optOut(productID, {
            ...this.state.signage,
            datedOn: Dates.formatOutgoingDate(this.state.signage.datedOn)
        }, this.state.password));

        if (!success) {
            return false;
        }

        if (this.state.page === numberOfProducts) {
            this.props.close();
        }

        this.setState({
            page: this.state.page + 1,
            showOptOut: false,
            visibility: false,
            password: '',
            signage: {
                name: '',
                signature: '',
                acceptedTerms: false,
                timestampOfAcceptance: null,
                datedOn: null
            }
        });

        return null;
    };

    _optIn = async (goToMarketplace = true) => {
        const productID = this.props.productsToReview[this.state.page - 1];
        const numberOfProducts = this.props.productsToReview.length;

        // send the opt out request to the backend
        const success = await this.props.dispatch(optIn(productID));

        if (!success) {
            return false;
        }

        if (this.state.page === numberOfProducts) {
            this.props.close();

            if (goToMarketplace) {
                window.location.href = '/marketplace';
            }

            return null;
        }

        this.setState({
            page: this.state.page + 1
        });

        return null;
    };

    _validateSignageBlock = async () => {
        const { signage, password } = this.state;

        const passwordError = await Services.validateInput('checkEmpty', password);
        let signageNameError = await Services.validateInput('checkEmpty', signage.name);
        const signageSignatureError = await Services.validateInput('checkEmpty', signage.signature);
        const signageDatedOnError = await Services.validateInput('date', signage.datedOn, i18n.locale);
        const signageAcceptedError = !signage.acceptedTerms;

        if (signageAcceptedError) {
            alert(i18n.t(tokens.labels.signage.pleaseAcceptAndAcknowledgeChanges));
            return null;
        }

        if (signage.name !== signage.signature) {
            signageNameError = i18n.t(tokens.labels.signage.signageNameError);
        }

        this.setState({
            signageNameError,
            signageSignatureError,
            signageDatedOnError,
            passwordError
        });

        if (
            signageNameError ||
            signageSignatureError ||
            signageDatedOnError ||
            passwordError
        ) {
            return false;
        }

        return true;
    };

    _toggleVisibility = () => {
        this.setState({
            visibility: !this.state.visibility
        });
    };

    _handleInputChange = (parentLevel, type, value) => {
        if (parentLevel) {
            this.setState({
                [parentLevel]: {
                    ...this.state[parentLevel],
                    [type]: value
                }
            });
        } else {
            this.setState({
                [type]: value
            });
        }
    };

    render() {
        const { whitelabel, productsToReview, marketplace } = this.props;

        const productID = this.state.page === 0 ? null : productsToReview[this.state.page - 1];
        const product = this.state.page === 0 ? null : marketplace.availableProducts[productID];
        const numberOfProducts = this.props.productsToReview.length;

        if (!product && this.state.page > 0) {
            return null;
        }

        const productName = `${ product.type.toUpperCase() === 'VOLUNTARY' ? i18n.t(tokens.labels.marketplace.voluntary) : i18n.t(tokens.labels.marketplace.optional) } ${ product.name.primary[this.props.meta.language] }`;

        if (this.state.showOptOut) {
            return (
                <FormModal
                    testid="marketplace-optOut-modal"
                    open={true}
                    title={ i18n.t(tokens.labels.marketplace.optOutConfirmation) }
                    disableBackdropClick
                    disableEscapeKeyDown
                    onSave={this._optOut}
                    maxWidth="lg"
                    customContainerStyle={{ maxWidth: 800 }}
                    customFormStyle={{ width: 700 }}
                    onBack={ () => {
                        this.setState({ showOptOut: false });
                    }}
                >
                    <Text
                        align="left"
                        small
                    >
                        { i18n.t(tokens.labels.marketplace.optOutConfirmationMessage, { product: productName }) }
                    </Text>

                    <TextInput
                        label={ i18n.t(tokens.labels.password) }
                        value={this.state.password}
                        inputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {
                                        this.state.visibility ?
                                            <VisibilityIcon
                                                style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                onClick={this._toggleVisibility}
                                            />
                                            :
                                            <VisibilityOffIcon
                                                style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                onClick={this._toggleVisibility}
                                            />
                                    }
                                </InputAdornment>
                            )
                        }}
                        onChange={(e) => this._handleInputChange(null, 'password', e.target.value)}
                        error={Boolean(this.state.passwordError)}
                        helperText={this.state.passwordError}
                        secure={!this.state.visibility}
                        fullWidth
                        autofocus
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.yourName) }
                        fullWidth
                        value={this.state.signage.name}
                        onChange={(e) => this._handleInputChange('signage', 'name', e.target.value)}
                        error={ this.state.signageNameError }
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.signature) }
                        fullWidth
                        value={this.state.signage.signature}
                        onChange={(e) => this._handleInputChange('signage', 'signature', e.target.value)}
                        customInputStyle={{
                            fontFamily: 'AntaraDistance !important',
                            fontWeight: 'bold',
                            fontSize: 20,
                            paddingBottom: 0
                        }}
                        error={ this.state.signageSignatureError }
                    />

                    <DateInput
                        label={ i18n.t(tokens.labels.datedOn) }
                        fullWidth
                        value={this.state.signage.datedOn}
                        onChange={(e) => this._handleInputChange('signage', 'datedOn', e.target.value)}
                        error={ this.state.signageDatedOnError }
                    />

                    <FormGroup row style={{ marginTop: 20 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ this.state.signage.acceptedTerms }
                                    onChange={ () => this._handleInputChange('signage', 'acceptedTerms', !this.state.signage.acceptedTerms) }
                                    value="acceptedTerms"
                                />
                            }
                            label={ i18n.t(tokens.labels.marketplace.optOutPhrase, { product: productName, carrier: product.carrier[this.props.meta.language] }) }
                        />
                    </FormGroup>

                    {this.props.loading && <Loader whitelabel={ whitelabel } customImageStyle={{ top: '50%' }} />}
                </FormModal>
            );
        }

        if (this.state.page === 0) {
            return (
                <FormModal
                    testid="marketplace-product-modal-main"
                    open={true}
                    title={ numberOfProducts === 1 ? i18n.t(tokens.labels.marketplace.newProductAvailable) : i18n.t(tokens.labels.marketplace.newProductsAvailable) }
                    disableBackdropClick
                    disableEscapeKeyDown
                    onSave={() => {
                        this.setState({
                            page: 1
                        });
                    }}
                    saveButtonText={ i18n.t(tokens.buttons.continue) }
                    maxWidth="lg"
                    customContainerStyle={{ maxWidth: 800 }}
                    customFormStyle={{ width: 700 }}
                >
                    <Text medium margin="0 0 15px 0">
                        {
                            numberOfProducts === 1 ?
                                i18n.t(tokens.labels.marketplace.announcement)
                                :
                                i18n.t(tokens.labels.marketplace.announcements)
                        }
                    </Text>

                    <Text medium margin="0 0 15px 0">
                        { i18n.t(tokens.labels.marketplace.additionalProductsInfo) }
                    </Text>

                    <Text medium margin="0 0 15px 0">
                        {
                            numberOfProducts === 1 ?
                                i18n.t(tokens.labels.marketplace.learnMoreButton)
                                :
                                i18n.t(tokens.labels.marketplace.learnMoreButtonMultiple)
                        }
                    </Text>

                    {this.props.loading && <Loader whitelabel={ whitelabel } customImageStyle={{ top: '50%' }} />}
                </FormModal>
            );
        }

        return (
            <FormModal
                testid="marketplace-product-modal"
                open={true}
                title={ i18n.t(tokens.titles.areYouInterested) }
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth="lg"
                customContainerStyle={{ maxWidth: 800 }}
                customFormStyle={{ width: 700 }}
                saveButtonText={ i18n.t(tokens.buttons.imInterested) }
                removeButtonText={ product.requiresOptIn ? i18n.t(tokens.buttons.optOut) : i18n.t(tokens.buttons.maybeLater) }
                onRemove={ () => {
                    if (product.requiresOptIn) {
                        this.setState({
                            showOptOut: true
                        });
                    } else {
                        this._optIn(false);
                    }
                }}
                onSave={ this._optIn }
            >
                <Text mediumLarge bold align="center">{ productName }</Text>
                <Text small light margin="0 0 15px 0" align="center">{ product.carrier[this.props.meta.language] }</Text>

                <Text medium margin="0 0 15px 0">{ product.description[this.props.meta.language] }</Text>

                {
                    product.requiresOptIn &&
                    <>
                        <Divider style={{ margin: 20 }}/>
                        <Text medium margin="0 0 15px 0">{ i18n.t(tokens.labels.marketplace.optInOutButton, { product: product.name.primary[this.props.meta.language] }) }</Text>
                        <Text medium margin="0 0 15px 0">{ i18n.t(tokens.labels.marketplace.optOutWarning, { product: product.name.primary[this.props.meta.language] }) }</Text>
                    </>
                }

                {this.props.loading && <Loader whitelabel={ whitelabel } customImageStyle={{ top: '50%' }} />}
            </FormModal>
        );
    }
}

ProductOptOutModal.propTypes = {
    loading: PropTypes.bool,
    meta: PropTypes.object,
    whitelabel: PropTypes.object,
    marketplace: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loading: state.marketplace.loading,
    meta: state.user.meta,
    whitelabel: state.whitelabel,
    marketplace: state.marketplace
});

export default connect(mapStateToProps)(ProductOptOutModal);